import { Layout } from 'src/common/components/Layout';
import React, { useMemo, useState } from 'react';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import { StaticPanelLeft } from 'src/public/components/static-page/StaticPanelLeft';
import { VendorGetStaticProps } from 'vendor/utils/VendorGetStaticProps';
import {
  aboutUsStaticPageQueryKey,
  useAboutUsStaticPageQuery,
} from 'src/common/hooks/staticPage/useAboutUsStaticPageQuery';
import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver';
import Observer from '@researchgate/react-intersection-observer';
import { ScrollOffset } from 'src/common/components/ScrollOffset';
import styled from 'styled-components';
import { AboutUsWhatIsPFPSection } from 'src/public/components/about-us/AboutUsWhatIsPFPSection';
import { AboutUsStrengthsSection } from 'src/public/components/about-us/AboutUsStrenghtsSection';
import { AboutUsKnowledgeSection } from 'src/public/components/about-us/AboutUsKnowledgeSection';
import { AboutUsBenefitSection } from 'src/public/components/about-us/AboutUsBenefitsSection';
import { HowItWorks } from 'src/common/components/HowItWorks';
import { Speakers } from 'src/common/components/Speakers';
import { AboutUsPossibilitiesSection } from 'src/public/components/about-us/AboutUsPossibilitiesSection';
import { AboutUsAdditionalOptions } from 'src/public/components/about-us/AboutUsAdditionalOptions';
import { StaticPagePanelItem } from 'src/common/models/staticPage';
import { getAboutUsStaticPage } from 'src/common/services/staticPage';
import getConfig from 'next/config';

const Container = styled.div`
  margin: 0 -1.5rem;
`;

const Section = styled.div`
  position: relative;
`;

const StyledHowItWorks = styled(HowItWorks)`
  margin: 0;
  max-width: 100%;
`;

const AboutUsPage = () => {
  useResetRedirectUrl();

  const [activeCategory, setActiveCategory] = useState<string>();

  const { data } = useAboutUsStaticPageQuery();

  const panelItems = useMemo<StaticPagePanelItem[] | undefined>(
    () =>
      data && [
        { id: 'whatIsPfp', title: data.data.attributes.whatIsPfp.heading },
        { id: 'strengths', title: data.data.attributes.strengths.heading },
        { id: 'knowledge', title: data.data.attributes.knowledge.heading },
        { id: 'benefits', title: data.data.attributes.benefits.heading },
        { id: 'howItWorks', title: 'Jak działa platforma szkoleń' },
        { id: 'speakers', title: 'Poznaj naszych wykładowców' },
        { id: 'possibilities', title: data.data.attributes.possibilities.heading },
        { id: 'additional', title: data.data.attributes.additional.heading },
      ],
    [data],
  );

  const { handleIntersection, intersectionThreshold } = useIntersectionObserver({
    items: panelItems?.map((item) => item.id) ?? [],
    onChange: setActiveCategory,
  });

  if (!data) {
    return null;
  }

  return (
    <Layout
      leftPanel={<StaticPanelLeft items={panelItems} activeItem={activeCategory} />}
      rightPanel={null}
      scrollToTopButton
      title="O nas"
    >
      <LayoutBox tabletBackground>
        <Container>
          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'whatIsPfp');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="whatIsPfp" />
              <AboutUsWhatIsPFPSection content={data.data.attributes.whatIsPfp} />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'strengths');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="strengths" />
              <AboutUsStrengthsSection content={data.data.attributes.strengths} />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'knowledge');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="knowledge" />
              <AboutUsKnowledgeSection content={data.data.attributes.knowledge} />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'benefits');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="benefits" />
              <AboutUsBenefitSection content={data.data.attributes.benefits} />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'howItWorks');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="howItWorks" />
              <StyledHowItWorks noShadow />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'speakers');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="speakers" />
              <Speakers layoutBox />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'possibilities');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="possibilities" />
              <AboutUsPossibilitiesSection content={data.data.attributes.possibilities} />
            </Section>
          </Observer>

          <Observer
            onChange={(entry) => {
              handleIntersection(entry, 'additional');
            }}
            threshold={intersectionThreshold}
          >
            <Section>
              <ScrollOffset id="additional" />
              <AboutUsAdditionalOptions content={data.data.attributes.additional} />
            </Section>
          </Observer>
        </Container>
      </LayoutBox>
    </Layout>
  );
};

export const getStaticProps = VendorGetStaticProps(async (ctx) => {
  const serverConfig = getConfig().serverRuntimeConfig;

  await ctx.queryClient.prefetchQuery(aboutUsStaticPageQueryKey, () => getAboutUsStaticPage());

  return {
    props: {},
    revalidate: serverConfig.staticPage.revalidate,
  };
});

export default AboutUsPage;
